import { useCallback, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { apiClient } from '@shared/api/client'
import { endpoints } from '@shared/api/endpoints'
import { QUERY_CACHE_KEYS } from '@shared/api/queryKeys'
import { buildQueryParams, QUIET_QUERY, usePagination } from '@shared/lib'
import {
  parsePaymentHistory,
  type PaymentHistoryPage,
  type PaymentHistoryResponse,
} from '../model/historyPage'
import type {
  Balance,
  PaymentProvider,
  PaymentStatus,
  TopUpRequest,
  TopUpResponse,
} from '../model/types'

/** Одна страница истории платежей участника; без `params` — без пагинации. */
async function fetchParticipantHistory(
  params?: Record<string, string>
): Promise<PaymentHistoryPage> {
  const r = await apiClient.get<PaymentHistoryResponse>(endpoints.payments.participantHistory, {
    params,
  })
  return parsePaymentHistory(r.data)
}

export function useParticipantBalance() {
  return useQuery<Balance>({
    queryKey: QUERY_CACHE_KEYS.payments.participantBalance,
    queryFn: () =>
      apiClient.get<Balance>(endpoints.payments.participantBalance).then((r) => r.data),
  })
}

export function useParticipantPaymentHistory() {
  const { page, pageSize, setTotalItems, resetPage, ...pagination } = usePagination({
    key: QUERY_CACHE_KEYS.payments.participantHistory.join('-'),
  })

  const queryParams = buildQueryParams({ page, page_size: pageSize })

  const query = useQuery<PaymentHistoryPage>({
    queryKey: [...QUERY_CACHE_KEYS.payments.participantHistory, queryParams],
    queryFn: () => fetchParticipantHistory(queryParams),
  })

  useEffect(() => {
    if (query.data?.count != null) setTotalItems(query.data.count)
  }, [query.data?.count, setTotalItems])

  return {
    data: query.data?.results ?? [],
    count: query.data?.count ?? 0,
    isLoading: query.isLoading,
    pagination: { page, pageSize, setTotalItems, resetPage, ...pagination },
  }
}

export function useTopUp() {
  const qc = useQueryClient()
  return useMutation<TopUpResponse, unknown, TopUpRequest>({
    mutationFn: (body) =>
      apiClient.post<TopUpResponse>(endpoints.payments.participantTopup, body).then((r) => r.data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: QUERY_CACHE_KEYS.payments.participantBalance }),
    // Тост показывает страница: свой на отказ и на ответ без адреса оплаты.
    meta: { silent: true },
  })
}

export function usePaymentProviders() {
  return useQuery<PaymentProvider[]>({
    queryKey: QUERY_CACHE_KEYS.payments.providers,
    // Ошибку показывает страница на месте кнопок, глобальный тост не нужен.
    ...QUIET_QUERY,
    queryFn: () =>
      apiClient.get<PaymentProvider[]>(endpoints.payments.providers).then((r) => r.data),
  })
}

/** Пометить устаревшими баланс и историю платежей: всё, что меняет кошелёк участника. */
export function invalidateParticipantPayments(qc: QueryClient): Promise<unknown[]> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: QUERY_CACHE_KEYS.payments.participantBalance }),
    qc.invalidateQueries({ queryKey: QUERY_CACHE_KEYS.payments.participantHistory }),
  ])
}

/**
 * Перечитать баланс и историю платежей. Нужна после сбоя `topup/` (заказ мог создаться, а
 * ответ не дойти) и после возврата с оплаты.
 */
export function useRefreshParticipantPayments() {
  const qc = useQueryClient()
  return useCallback(() => invalidateParticipantPayments(qc), [qc])
}

interface PaymentRowStatusOptions {
  enabled: boolean
  /** Период опроса в мс по последнему статусу из этого же запроса; `false` — не опрашивать. */
  refetchInterval: (status: PaymentStatus | null | undefined) => number | false
}

/**
 * Статус одной строки истории по её `id`; `null` — строки нет.
 *
 * Запрос без `page`: таблица может стоять не на первой странице, а статус нужен по заказу.
 * Ключ лежит под корнем истории, чтобы общая инвалидация после оплаты его тоже задевала.
 * Сбой опроса тост не показывает: следующий тик повторит запрос.
 */
export function usePaymentRowStatus(id: number | null, options: PaymentRowStatusOptions) {
  return useQuery<PaymentStatus | null>({
    queryKey: [...QUERY_CACHE_KEYS.payments.participantHistory, 'status', id],
    ...QUIET_QUERY,
    enabled: options.enabled && id !== null,
    refetchInterval: (query) => options.refetchInterval(query.state.data),
    queryFn: async () => {
      const page = await fetchParticipantHistory()
      return page.results.find((p) => p.id === id)?.status ?? null
    },
  })
}
